import { motion } from "framer-motion";
import { Code, Briefcase, Stethoscope, Scale, Camera, GraduationCap, Palette, Building2, Megaphone, Wrench, TrendingUp, Users } from "lucide-react";

const personas = [
  {
    icon: Code,
    title: "Digital & Numérique",
    description: "Montrez vos projets, vos résultats et votre expertise dans le digital.",
    color: "from-primary/20 to-accent/10",
  },
  {
    icon: Briefcase,
    title: "Consultants & Managers",
    description: "Mettez en avant vos missions, résultats chiffrés et recommandations clients.",
    color: "from-accent/20 to-primary/10",
  },
  {
    icon: Palette,
    title: "Créatifs & Designers",
    description: "Portfolio visuel, études de cas, processus créatif — séduisez vos futurs clients.",
    color: "from-primary/15 to-accent/15",
  },
  {
    icon: Megaphone,
    title: "Marketing & Communication",
    description: "Campagnes, KPIs, stratégies — prouvez votre impact avec des résultats concrets.",
    color: "from-accent/15 to-primary/20",
  },
  {
    icon: Scale,
    title: "Juridique & Finance",
    description: "Expertises, domaines de compétence, publications — inspirez confiance immédiatement.",
    color: "from-primary/20 to-accent/15",
  },
  {
    icon: Stethoscope,
    title: "Santé & Bien-être",
    description: "Spécialités, parcours, témoignages patients — rassurez avant le premier rendez-vous.",
    color: "from-accent/20 to-primary/15",
  },
  {
    icon: Building2,
    title: "Immobilier & BTP",
    description: "Réalisations, chantiers, références — montrez votre savoir-faire en images.",
    color: "from-primary/15 to-accent/20",
  },
  {
    icon: Camera,
    title: "Photo & Vidéo",
    description: "Galeries, projets clients, showreel — laissez votre travail parler pour vous.",
    color: "from-accent/10 to-primary/20",
  },
  {
    icon: TrendingUp,
    title: "Freelances & Indépendants",
    description: "Services, tarifs, témoignages — convertissez vos visiteurs en clients.",
    color: "from-primary/20 to-accent/10",
  },
  {
    icon: Users,
    title: "RH & Formation",
    description: "Programmes, certifications, retours apprenants — valorisez votre pédagogie.",
    color: "from-accent/15 to-primary/15",
  },
  {
    icon: Wrench,
    title: "Artisans & Métiers manuels",
    description: "Photos avant/après, avis clients, zones d'intervention — remplissez votre carnet.",
    color: "from-primary/15 to-accent/10",
  },
  {
    icon: GraduationCap,
    title: "Étudiants & Juniors",
    description: "Projets perso, stages, formations — démarquez-vous des autres candidats.",
    color: "from-accent/20 to-primary/20",
  },
];

const SellTargetAudience = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            🎯 Pour qui ?
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Conçu pour{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              tous les métiers
            </span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Quel que soit votre secteur, votre portfolio sera parfaitement adapté à votre activité
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 max-w-7xl mx-auto">
          {personas.map((persona, i) => (
            <motion.div
              key={persona.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="group relative"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${persona.color} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl`} />
              <div className="relative p-6 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 hover:border-primary/40 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-accent/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <persona.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-base mb-1.5">{persona.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{persona.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SellTargetAudience;
